/* The builder's day host — the 574px panel of `Builder planu - tydzien - programator`, and
 * everything the day modal beside it shares with it.
 *
 * ── One host, one sheet, and the modal's sheet holds only its BOX ───────────────────────────
 * `DayPanelHost` is one component in one tree position: the same form, the same drawer, the same
 * drawer scrim, in whichever box the trainer asked for. So the parts that do not change with the box
 * are declared here ONCE — `.form`, `.libraryScrim`, `.drawer`, `.drawerClosed` — and
 * `DayPanelModal.module.css` carries the modal's own geometry and nothing else. They were two
 * byte-identical copies until 13 September 2026, under a header claiming they differed.
 *
 * What the box DOES get to say about the shared parts, it says through a custom property:
 * `--scrim-library` is the drawer scrim's ink, transparent in the panel and a real dim in the modal.
 * A modifier class declared in the other sheet would be one class against one class, settled by
 * whichever chunk the build happened to emit last.
 *
 * ── Why this is not `shared/ui/DayDockFrame.module.css` ─────────────────────────────────────
 * That module is the CALENDAR's dock and stays exactly as it is: 604px, a library column standing
 * outside the form at 240px, and the day-programming canon's proportions. The 07.09 programator
 * handoff redraws the builder's panel at 574px with the library as a DRAWER over it, so the two
 * surfaces are no longer one surface and a shared stylesheet would have to branch on which host is
 * asking — which is the near-duplicate this repo's rules exist to prevent, wearing a different hat.
 * The builder therefore declares its own geometry here and imports none of that file.
 *
 * ONE declaration is carried across rather than inherited, and it is the one that file's header
 * argues for at length: `line-height: var(--lh-auto)`. `body` sets `--lh-relaxed` (1.5) app-wide,
 * which is prose leading; forty-odd micro-labels in this dock inheriting it accumulated until the
 * library rail beside them started 3.5px below where the canon draws it. Duplicating one
 * declaration with the reason attached is right; duplicating the block would not be.
 *
 * ── It overlays the week and does not push it ───────────────────────────────────────────────
 * The mock positions the panel `absolute` inside its own frame; this app portals the popup into
 * `<body>`, where `absolute` would resolve against the initial containing block and scroll away
 * with the page. `fixed` is the same drawing with the same edges, and it is what the calendar's
 * dock already does. Nothing behind it reflows — which is exactly why `useRevealOpenDay` exists.
 */

.panel {
  position: fixed;
  z-index: 20;
  inset-block: 0;
  inset-inline-end: 0;
  display: flex;
  flex-direction: column;
  width: var(--w-builder-panel);
  max-width: 100vw;
  overflow: hidden;
  background: var(--bg-sunken);
  border-inline-start: var(--border-hairline) solid var(--border-2);

  /* A HORIZONTAL cast, neutral ink. The panel arrives from the right edge, so a downward card
   * shadow under a full-height surface would read as a seam rather than as a layer. */
  box-shadow: var(--shadow-sheet);

  /* See the header. */
  line-height: var(--lh-auto);

  /* The drawer scrim's ink in THIS box: none. The mock draws no dimming inside the panel, and the
   * 254px of panel the drawer does not cover is a strip rather than a surface. The modal says
   * otherwise — see `DayPanelModal.module.css`. */
  --scrim-library: transparent;

  /* The slide. Transform only, so nothing behind the panel is laid out again as it arrives. */
  transition: transform var(--dur-panel) var(--ease-fp);
}

/* With the builder's Buddy panel open the day panel stands to its left, both overlaying the week
 * under the top bar (owner, 7 October 2026). */
.panelBesideAssistant {
  inset-block-start: var(--h-app-bar);
  inset-inline-end: var(--w-buddy-panel);
}

/* The mock's closed state, reached through Base UI's own transition hooks rather than through a
 * flag: the popup is mounted only while the dock is open, and these two attributes are what let it
 * slide in on arrival and back out before it unmounts. 102% rather than 100% so the elevation
 * clears the edge with the panel. */
.panel[data-starting-style],
.panel[data-ending-style] {
  transform: translateX(102%);
  pointer-events: none;
}

/* ── The form, and the library over it ───────────────────────────────────────────────────── */

.form {
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

/* The drawer's scrim. A real BUTTON rather than a decorative box: putting the library away has to
 * have a pointer path and a keyboard path, and this is both.
 *
 * It spans the WHOLE host, under the drawer, which is the mock's own arrangement — its scrim sits
 * at 29 beneath a drawer at 30. That z-order is what makes a full-inset scrim safe: a press in the
 * middle reaches the drawer above it, and a press on the host the drawer does not cover lands here
 * and dismisses.
 *
 * It started where the drawer ENDS between 7 September 2026 and 13 September, on the reading that a
 * full-panel scrim would swallow presses aimed at the drawer. It does not — the drawer paints and
 * hits above it — and the cost of the narrower box was worse than the bug it was avoiding: the
 * exposed strip stayed inert but looked live, so a trainer aiming at `+ Seria` under it hit nothing
 * at all rather than putting the library away.
 *
 * Its INK is the box's, through `--scrim-library`. Transparent over 254px of panel is a strip a
 * trainer reads as the edge of the drawer; transparent over 702px of modal is a lit, dead day —
 * every other overlay in this app puts what it covers visibly behind a layer, and the modal does
 * too. */
.libraryScrim {
  position: absolute;
  z-index: 29;
  inset: 0;
  cursor: default;
  background: var(--scrim-library);
  border: 0;
}

/* The 320px library drawer. It arrives over the host's LEFT edge and covers the whole of it — the
 * mock's own behaviour — so everything behind it is marked `inert` by the component while it is out
 * rather than left visible, covered and still in the tab order. Always mounted once it has arrived,
 * because the rail behind it is the same catalogue read the host already held open and unmounting it
 * would slide an empty box out on the way back. */
.drawer {
  position: absolute;
  z-index: 30;
  inset-block: 0;
  inset-inline-start: 0;
  display: flex;
  width: var(--w-lib-drawer);
  background: var(--bg-card);
  border-inline-end: var(--border-hairline) solid var(--border-2);
  box-shadow: var(--shadow-drawer);
  transition: transform var(--dur-drawer) var(--ease-fp);
}

.drawerClosed {
  transform: translateX(-102%);
  pointer-events: none;
}
